import {
  Field,
  Input,
  Textarea,
  InputProps,
  TextareaProps,
  DropdownProps,
  Dropdown,
  OptionOnSelectData,
  ComboboxProps,
  Combobox,
  CheckboxProps,
  Checkbox,
  makeStyles,
  tokens,
  mergeClasses,
  ButtonProps,
  Button,
  PositioningShorthand,
  SearchBox,
  SearchBoxProps,
  Radio,
  RadioGroup,
  RadioGroupProps,
} from '@fluentui/react-components';
import CalendarDatePicker, { type TCalendarDatePickerProps } from './CalendarDatePicker';
import { localDateToYMD, ymdToLocalDate } from '@shared/utils/date';
import {
  forwardRef,
  LegacyRef,
  MutableRefObject,
  ReactNode,
  Ref,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Control,
  Controller,
  type ControllerProps,
  ControllerRenderProps,
  FieldErrors,
  useWatch,
} from 'react-hook-form';
import { MultiUserSelect, TMultiUserSelectProps } from '@modules/users';
import { ControlledTooltip } from './ControlledTooltip';
import { ArrowUploadRegular } from '@fluentui/react-icons';

export interface IControlledProps {
  // required: name: string;
  control: Control<any>;
  rules?: ControllerProps['rules'];
  errors: FieldErrors<any>;
  name: string;
  inputType?:
    | 'input'
    | 'searchbox'
    | 'number'
    | 'textarea'
    | 'dropdown'
    | 'combobox'
    | 'checkbox'
    | 'radio'
    | 'date'
    | 'multiuserselect'
    | 'file';
  label?: string;
  fixedHeight?: boolean;
  fieldClassName?: string;
  tooltip?: string;
  tooltipPosition?: PositioningShorthand;
  tooltipWithArrow?: boolean;
  required?: boolean;
  readonly?: boolean;
  className?: string;
}

interface IControlledInputProps extends InputProps, IControlledProps {
  name: string;
  inputType?: 'input' | 'number';
}

interface IControlledSearchBoxProps extends SearchBoxProps, IControlledProps {
  name: string;
  inputType?: 'searchbox';
}

interface IControlledTextareaProps extends TextareaProps, IControlledProps {
  name: string;
  inputType?: 'textarea';
  resize?: TextareaProps['resize'];
  /** enable auto-resizing to fit content */
  autoResize?: boolean;
}

interface IControlledCheckboxProps extends CheckboxProps, IControlledProps {
  name: string;
  label: string;
  inputType?: 'checkbox';
}

interface IControlledRadioProps extends RadioGroupProps, IControlledProps {
  name: string;
  inputType?: 'radio';
  options: { value: string; label: string }[];
}

interface IControlledDropdownProps extends DropdownProps, IControlledProps {
  name: string;
  getLabel: (value: any) => string;
  inputType?: 'dropdown';
}

interface IControlledComboboxProps extends ComboboxProps, IControlledProps {
  name: string;
  getLabel: (value: any) => string;
  inputType?: 'combobox';
  setQuery: (query: string) => void;
}

interface IControlledDatePickerProps extends TCalendarDatePickerProps, IControlledProps {
  name: string;
  inputType?: 'date';
  /** 'iso' stores ISO instants (default); 'day' stores 'YYYY-MM-DD' calendar days */
  valueFormat?: 'iso' | 'day';
}

interface IControlledMultiUserSelectProps
  extends Omit<TMultiUserSelectProps, 'label' | 'selectedUserIds' | 'setSelectedUserIds'>,
    IControlledProps {
  name: string;
  inputType?: 'multiuserselect';
  label: string;
  isSingle?: boolean;
}

interface IControlledFileUploadProps extends IControlledProps {
  name: string;
  inputType?: 'file';
  /** displayed on the button (defaults to “Select file”) */
  // eslint-disable-next-line no-undef
  children?: ReactNode;
  /** HTML accept attr (e.g. "application/pdf") */
  accept?: string;
  multiple?: boolean;
  /** optional button props from <Button /> like { appearance: 'primary', icon: <ArrowUploadRegular /> } */
  // eslint-disable-next-line no-undef
  buttonProps?: Omit<ButtonProps, 'onClick' | 'children'>;
  disabled?: boolean;
}

const FileButton = ({
  field,
  props,
  hiddenFileInput,
  name,
}: {
  field: ControllerRenderProps<any, string>;
  props: IControlledFileUploadProps;
  hiddenFileInput: MutableRefObject<HTMLInputElement | null>;
  name: string;
}) => {
  /* show file name once selected */
  let btnLabel = props.children?.toString() ?? 'Select file';
  if (field.value?.length) {
    btnLabel = field.value[0]?.name;
    if (btnLabel.length > 30) btnLabel = btnLabel.slice(0, 30) + '…';
  }

  const handleClick = () => {
    requestAnimationFrame(() => {
      if (hiddenFileInput.current) {
        hiddenFileInput.current.click();
      }
    });
  };

  return (
    <>
      <Button
        type="button"
        className="w-fit"
        icon={<ArrowUploadRegular />}
        // prevent losing focus in RHF forms
        onClick={handleClick}
        {...(props.buttonProps as ButtonProps)}
      >
        {btnLabel}
      </Button>

      <input
        ref={hiddenFileInput}
        hidden
        type="file"
        name={name}
        accept={props.accept}
        multiple={props.multiple}
        onChange={(e) => {
          const files = e.target.files;
          if (files?.length) field.onChange(files);
        }}
      />
    </>
  );
};

const useStyles = makeStyles({
  readonly: {
    color: tokens.colorNeutralForeground1,
    '& input': {
      color: tokens.colorNeutralForeground1,
      cursor: 'not-allowed !important', // for date picker
    },
    '& textarea': {
      color: tokens.colorNeutralForeground1,
    },
    '& button': {
      color: tokens.colorNeutralForeground1,
    },
    '& .fui-Tag__dismissIcon': {
      display: 'none',
    },
    '& .fui-Tag__primaryText': {
      paddingRight: '6px',
    },
  },
});

const ControlledField = forwardRef(
  (
    {
      label,
      name,
      control,
      rules,
      errors,
      inputType = 'input',
      fixedHeight = false,
      fieldClassName = '',
      tooltip = '',
      tooltipPosition,
      tooltipWithArrow = false,
      required = false,
      readonly = false,
      ...inputProps
    }:
      | IControlledInputProps
      | IControlledSearchBoxProps
      | IControlledTextareaProps
      | IControlledCheckboxProps
      | IControlledRadioProps
      | IControlledDropdownProps
      | IControlledComboboxProps
      | IControlledDatePickerProps
      | IControlledMultiUserSelectProps
      | IControlledFileUploadProps,
    ref
  ) => {
    const classes = useStyles();
    if (readonly) {
      inputProps.className = mergeClasses(inputProps.className, classes.readonly);
      inputProps.disabled = true;
    }
    const isSingle = (inputProps as IControlledMultiUserSelectProps).isSingle;

    const hiddenFileInput = useRef<HTMLInputElement | null>(null);

    // --- textarea auto-resize ---
    // Track this field’s value so <useAutosizeRef> re-fires when RHF
    const fieldValue = useWatch({ control, name });
    const dateValue = useMemo(() => {
      if (inputType !== 'date' || !fieldValue) return null;
      // Existing forms saved ISO instants; preserve their local day when opening them.
      return fieldValue.length === 10 ? ymdToLocalDate(fieldValue) : new Date(fieldValue);
    }, [inputType, fieldValue]);
    // Build a callback-ref that
    //   - resizes immediately on mount   (in setRef)
    //   - resizes after every value change (via useLayoutEffect)
    //   - still forwards the DOM node if the parent supplied a ref.
    const autosizeRef = useAutosizeRef(
      ref as Ref<HTMLTextAreaElement>,
      inputType === 'textarea' && ((inputProps as IControlledTextareaProps).autoResize ?? false),
      fieldValue // <- dependency for live resizing
    );

    return (
      <Field label={inputType !== 'checkbox' ? label : undefined} className={fieldClassName} required={required}>
        <ControlledTooltip
          content={tooltip}
          relationship="description"
          positioning={tooltipPosition}
          withArrow={tooltipWithArrow}
        >
          <div className="grid">
            <Controller
              name={name}
              control={control}
              rules={rules}
              render={({ field }) => {
                return ['input', 'number'].includes(inputType) ? (
                  <Input
                    {...field}
                    inputType={inputType}
                    {...(inputProps as IControlledInputProps)}
                    ref={ref as LegacyRef<HTMLInputElement> | undefined}
                  />
                ) : inputType === 'searchbox' ? (
                  <SearchBox {...field} {...(inputProps as IControlledSearchBoxProps)} />
                ) : inputType === 'textarea' ? (
                  <Textarea {...field} {...(inputProps as IControlledTextareaProps)} ref={autosizeRef} />
                ) : inputType === 'checkbox' ? (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    {...(inputProps as IControlledCheckboxProps)}
                    label={label}
                    ref={ref as LegacyRef<HTMLInputElement> | undefined}
                  />
                ) : inputType === 'radio' ? (
                  (() => {
                    const { options, ...radioGroupProps } = inputProps as IControlledRadioProps;
                    return (
                      <RadioGroup
                        {...radioGroupProps}
                        value={field.value}
                        onChange={(_, data) => field.onChange(data.value)}
                      >
                        {options.map((option) => (
                          <Radio key={option.value} value={option.value} label={option.label} />
                        ))}
                      </RadioGroup>
                    );
                  })()
                ) : inputType === 'dropdown' ? (
                  <Dropdown
                    selectedOptions={field.value}
                    value={
                      Array.isArray(field.value) && field.value.length > 0
                        ? (() => {
                            const labels = field.value
                              .map((value) => (inputProps as IControlledDropdownProps).getLabel(value))
                              .filter(Boolean);
                            const head = labels.slice(0, 2).join(', ');
                            return head + (labels.length > 2 ? ', ...' : '');
                          })()
                        : ''
                    }
                    onOptionSelect={(_, data: OptionOnSelectData) => {
                      if (data) {
                        field.onChange(data.selectedOptions);
                      }
                    }}
                    {...(inputProps as IControlledDropdownProps)}
                    ref={ref as LegacyRef<HTMLButtonElement> | undefined}
                  />
                ) : inputType === 'combobox' ? (
                  <Combobox
                    {...(inputProps as IControlledComboboxProps)}
                    autoComplete="off"
                    selectedOptions={field.value}
                    onOptionSelect={(_, data: OptionOnSelectData) => {
                      if (data) {
                        field.onChange(data.selectedOptions);
                        if (data.selectedOptions.length > 0)
                          (inputProps as IControlledComboboxProps).setQuery(
                            (inputProps as IControlledComboboxProps).getLabel(data.selectedOptions[0])
                          );
                      }
                    }}
                    onChange={(ev) => (inputProps as IControlledComboboxProps).setQuery(ev.target.value)}
                    ref={ref as LegacyRef<HTMLInputElement> | undefined}
                  />
                ) : inputType === 'date' ? (
                  (() => {
                    const { valueFormat = 'iso', ...dateProps } = inputProps as IControlledDatePickerProps;
                    return (
                      <CalendarDatePicker
                        value={dateValue}
                        onSelectDate={(date) =>
                          field.onChange(
                            date ? (valueFormat === 'day' ? localDateToYMD(date) : date.toISOString()) : null
                          )
                        }
                        onBlur={field.onBlur}
                        {...dateProps}
                        ref={ref as LegacyRef<HTMLInputElement> | undefined}
                      />
                    );
                  })()
                ) : inputType === 'multiuserselect' ? (
                  <MultiUserSelect
                    selectedUserIds={field.value}
                    setSelectedUserId={isSingle ? field.onChange : undefined}
                    setSelectedUserIds={isSingle ? undefined : field.onChange}
                    {...(inputProps as IControlledMultiUserSelectProps)}
                    ref={ref}
                  />
                ) : inputType === 'file' ? (
                  <FileButton
                    field={field}
                    props={inputProps as IControlledFileUploadProps}
                    hiddenFileInput={hiddenFileInput}
                    name={name}
                  />
                ) : (
                  <>Error</>
                );
              }}
            />
          </div>
        </ControlledTooltip>
        {errors[name] ? (
          // @ts-expect-error
          <p className="text-red-500">{errors[name].message}</p>
        ) : fixedHeight ? (
          <p>
            <br />
          </p>
        ) : null}
      </Field>
    );
  }
);

// --- helpers ---
function adjustHeight(el: HTMLTextAreaElement) {
  if (!el || !('scrollHeight' in el)) return;
  el.style.height = 'auto'; // reset height to auto to shrink if needed
  el.style.height = `${el.scrollHeight}px`; // set height to scrollHeight to fit content
}

// --- hooks ---
function useAutosizeRef(
  externalRef: Ref<HTMLTextAreaElement>,
  enabled: boolean,
  value: unknown // field value (watched)
) {
  const inner = useRef<HTMLTextAreaElement | null>(null);

  // Adjust after every render where the value changed.
  useLayoutEffect(() => {
    if (enabled && inner.current) adjustHeight(inner.current);
  }, [enabled, value]);

  // Callback ref: fires once on mount (before first paint).
  const setRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      inner.current = node;
      if (enabled && node) adjustHeight(node); // first resize

      // Forward to parent ref, if any
      if (typeof externalRef === 'function') externalRef(node);
      else if (externalRef) (externalRef as MutableRefObject<HTMLTextAreaElement | null>).current = node;
    },
    [enabled, externalRef]
  );

  return setRef;
}

export { ControlledField };
